import { UAParser } from 'ua-parser-js'

interface DeviceInfo {
  os: {
    name?: string
    version?: string
  }
  browser: {
    major?: string
    name?: string
    version?: string
  }
  device: {
    model?: string
    type?: string
    vendor?: string
  }
}

export const getDeviceInfo = (): DeviceInfo => {
  const parser = new UAParser()
  const device = parser.getDevice()
  const browser = parser.getBrowser()
  const os = parser.getOS()

  return {
    os,
    device,
    browser,
  }
}

export const isCashAppWebView = (): boolean => {
  const parser = new UAParser()
  const userAgent = parser.getUA()

  // Logic taken from sq-cash-frontend: https://github.com/squareup/sq-cash-frontend/blob/907dc6f0c973b27f699a9681457a1af188736d26/app/utils/ua-detector.js#L114
  // and combined with Taxes logic: https://github.com/squareup/tax_web-fe/blob/a9b57d16e48e1c649d33f872228770ba26d26f65/libs/common/src/utils/browserChecker.ts#L11
  return /cash app webview|Cash App|com\.squareup\.cash|cashapp/i.test(userAgent)
}

export const isMobileDevice = () => {
  const { device } = getDeviceInfo()
  return device.type === 'mobile'
}

export const isMobileDeviceOrTablet = () => {
  const { device } = getDeviceInfo()
  return device?.type === 'mobile' || device?.type === 'tablet'
}

export const isAndroid = () => {
  const { os } = getDeviceInfo()
  return !!os.name?.match(/android/i)
}

// Known in-app browser user agent tokens
const IN_APP_BROWSER_UA_REGEX =
  /FBAN|FBAV|FB_IAB|Instagram|Snapchat|MicroMessenger|Line\/|LinkedInApp|Twitter|BytedanceWebview|musical_ly|TikTok|WhatsApp|Pinterest|Telegram|Threads|Reddit/i

// Android WebViews include "; wv)" in the UA string
const ANDROID_WEBVIEW_REGEX = /; wv\)/

// Known third-party iOS browsers that lack the Safari token but are not WebViews
const IOS_BROWSERS_WITHOUT_SAFARI_TOKEN = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//

/**
 * Detects whether the current browser is an in-app browser or WebView.
 *
 * Uses three detection layers:
 * 1. Known in-app browser UA tokens (Facebook, Instagram, TikTok, etc.)
 * 2. Android WebView marker ("; wv)" in the UA string)
 * 3. iOS WebView heuristic (AppleWebKit present without Safari token)
 */
export const isInAppBrowser = (): boolean => {
  const ua = new UAParser().getUA()

  if (isCashAppWebView()) return true

  if (IN_APP_BROWSER_UA_REGEX.test(ua)) return true

  if (ANDROID_WEBVIEW_REGEX.test(ua)) return true

  // iOS WebView heuristic: WKWebView includes AppleWebKit but omits the Safari token.
  // Exclude known iOS browsers (Chrome, Firefox, Edge, Opera, Google Search App)
  // that also lack the Safari token.
  const isIOS = /iPhone|iPad|iPod/.test(ua)
  const hasWebKit = /AppleWebKit/.test(ua)
  const hasSafariToken = /Safari\//.test(ua)
  if (isIOS && hasWebKit && !hasSafariToken && !IOS_BROWSERS_WITHOUT_SAFARI_TOKEN.test(ua)) {
    return true
  }

  return false
}
